/**
 * Centre Page team profiles — responsive scroll + dots carousel.
 *
 * Activation is decided per breakpoint (data attributes set by PHP):
 *  - Full width / mobile: carousel when there are more than 3 profiles.
 *  - Half width (desktop): carousel when there are more than 3 profiles below
 *    the manager (more than 4 total).
 * Below the threshold the track renders as a static grid with no dots.
 */
(function () {
	'use strict';

	var TABLET_BREAKPOINT = 768;
	var VISIBLE_DESKTOP = 3;
	var VISIBLE_MOBILE = 1;
	var SWIPE_THRESHOLD = 40;

	function onReady(fn) {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', fn);
		} else {
			fn();
		}
	}

	function debounce(fn, wait) {
		var timeout;
		return function () {
			var args = arguments;
			clearTimeout(timeout);
			timeout = setTimeout(function () {
				fn.apply(null, args);
			}, wait);
		};
	}

	function initCarousel(root) {
		var count = parseInt(root.getAttribute('data-count') || '0', 10);
		var carouselDesktop = root.getAttribute('data-carousel-desktop') === '1';
		var carouselMobile = root.getAttribute('data-carousel-mobile') === '1';

		if (count < 2 || (!carouselDesktop && !carouselMobile)) {
			return;
		}

		var viewport = root.querySelector('.team-carousel__viewport');
		var track = root.querySelector('.team-carousel__track');
		var dotsWrap = root.querySelector('.team-carousel__dots');

		if (!viewport || !track || !dotsWrap) {
			return;
		}

		var cards = Array.prototype.slice.call(track.querySelectorAll('.profile-card'));
		if (!cards.length) {
			return;
		}

		var currentIndex = 0;
		var renderedSlideCount = -1;
		var touchStartX = 0;
		var touchStartY = 0;

		function isMobile() {
			return window.innerWidth < TABLET_BREAKPOINT;
		}

		function isActive() {
			return isMobile() ? carouselMobile : carouselDesktop;
		}

		function visiblePerView() {
			return isMobile() ? VISIBLE_MOBILE : VISIBLE_DESKTOP;
		}

		function slideCount() {
			return Math.max(1, count - visiblePerView() + 1);
		}

		function getTrackGap() {
			var styles = window.getComputedStyle(track);
			var gapValue = styles.columnGap;
			if (!gapValue || 'normal' === gapValue) {
				gapValue = styles.gap;
			}
			return parseFloat(gapValue || '0') || 0;
		}

		function getCardStep() {
			var gap = getTrackGap();
			return cards[0] ? cards[0].offsetWidth + gap : 0;
		}

		function applyTransform() {
			var step = getCardStep();
			if (step <= 0 || 0 === currentIndex) {
				track.style.transform = '';
				return;
			}
			// Round to whole pixels to avoid WebKit subpixel text blur on slides.
			track.style.transform = 'translate3d(-' + Math.round(currentIndex * step) + 'px, 0, 0)';
		}

		function updateDots() {
			var dots = dotsWrap.querySelectorAll('.team-carousel__dot');
			Array.prototype.forEach.call(dots, function (dot, index) {
				var active = index === currentIndex;
				dot.classList.toggle('is-active', active);
				dot.setAttribute('aria-selected', active ? 'true' : 'false');
			});
		}

		function buildDots(total) {
			dotsWrap.innerHTML = '';

			for (var i = 0; i < total; i += 1) {
				var dot = document.createElement('button');
				dot.type = 'button';
				dot.className = 'team-carousel__dot' + (0 === i ? ' is-active' : '');
				dot.setAttribute('role', 'tab');
				dot.setAttribute('aria-selected', 0 === i ? 'true' : 'false');
				dot.setAttribute('aria-label', 'Team slide ' + (i + 1));
				dot.setAttribute('data-slide-index', String(i));
				dot.addEventListener('click', function (event) {
					goTo(parseInt(event.currentTarget.getAttribute('data-slide-index') || '0', 10));
				});
				dotsWrap.appendChild(dot);
			}

			renderedSlideCount = total;
		}

		function goTo(index) {
			currentIndex = Math.max(0, Math.min(slideCount() - 1, index));
			applyTransform();
			updateDots();
		}

		function deactivate() {
			root.classList.remove('is-carousel');
			dotsWrap.hidden = true;
			track.style.transform = '';
			renderedSlideCount = -1;
		}

		function activate() {
			root.classList.add('is-carousel');
			dotsWrap.hidden = false;

			var total = slideCount();
			if (total !== renderedSlideCount) {
				buildDots(total);
			}

			if (currentIndex > total - 1) {
				currentIndex = total - 1;
			}

			applyTransform();
			updateDots();
		}

		function refresh() {
			if (isActive()) {
				activate();
			} else {
				deactivate();
			}
		}

		viewport.addEventListener(
			'touchstart',
			function (event) {
				if (!isActive() || !event.touches || !event.touches.length) {
					return;
				}
				touchStartX = event.touches[0].clientX;
				touchStartY = event.touches[0].clientY;
			},
			{ passive: true }
		);

		viewport.addEventListener(
			'touchend',
			function (event) {
				if (!isActive() || !event.changedTouches || !event.changedTouches.length) {
					return;
				}

				var deltaX = event.changedTouches[0].clientX - touchStartX;
				var deltaY = event.changedTouches[0].clientY - touchStartY;

				if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) {
					return;
				}

				goTo(currentIndex + (deltaX < 0 ? 1 : -1));
			},
			{ passive: true }
		);

		window.addEventListener(
			'resize',
			debounce(function () {
				refresh();
			}, 150)
		);

		refresh();
	}

	function init() {
		var carousels = document.querySelectorAll('.team-carousel[data-team-carousel]');
		Array.prototype.forEach.call(carousels, initCarousel);
	}

	onReady(init);
})();
